import * as Yup from 'yup';
import i18n from '../../i18n';
import { getDaysPerMonth, getYearsTillNow } from './datesHelpers';
import { formatMoney } from './numeralHelpers';

export const getValidationSchemaImpersonate = (
  {
    fields = [],
    // countries = [],
    minAmount = 1,
    maxAmount = 500000000,
    type,
    noValidation,
    // countryCode = '',
  } = {
    fields: [],
    minAmount: 1,
    maxAmount: 500000000,
    countries: [],
    countryCode: ',',
    type,
    noValidation,
  },
) => {
  const allValidationImpersonateBucket = {
    FirstName: Yup.string().nullable(),
    MiddleName: Yup.string().nullable(),
    LastName: Yup.string().nullable(),
    Suffix: Yup.object().nullable(),
    Month: Yup.string().when('Month', (val) => {
      if (val) {
        return Yup.number()
          .min(1, i18n.t(`fields.errors.validation.minMonth`))
          .max(12, i18n.t(`fields.errors.validation.maxMonth`));
      }
      return (
        Yup.string()
          .nullable(true)
          // checking self-equality works for NaN, transforming it to null
          .transform((_, val) => (val ? Number(val) : ''))
      );
    }),
    Year: Yup.string().when('Year', (val) => {
      if (val) {
        return Yup.number()
          .min(
            parseInt(
              getYearsTillNow().reverse()[
                getYearsTillNow().reverse().length - 1
              ],
              10,
            ),
            i18n.t(`fields.errors.validation.minYear`),
          )
          .max(
            parseInt(getYearsTillNow().reverse()[0], 10),
            i18n.t(`fields.errors.validation.maxYear`),
          );
      }
      return (
        Yup.string()
          .nullable(true)
          // checking self-equality works for NaN, transforming it to null
          .transform((_, val) => (val ? Number(val) : ''))
      );
    }),
    Day: Yup.string().when('Day', (val) => {
      if (val) {
        return Yup.number()
          .min(1, i18n.t(`fields.errors.validation.minDay`))
          .when(['Month', 'Year'], (Month, Year, schema) =>
            Month && Month > 0 && Month < 13 && Year
              ? schema.max(
                  getDaysPerMonth(
                    Month.toString().padStart(2, '0'),
                    Year.toString().padStart(2, '0'),
                  ).length,
                  i18n.t(`fields.errors.validation.maxDay`),
                )
              : schema.max(30, i18n.t(`fields.errors.validation.maxDay`)),
          );
      }
      return (
        Yup.string()
          .nullable(true)
          // checking self-equality works for NaN, transforming it to null
          .transform((_, val) => (val ? Number(val) : ''))
      );
    }),
    IdentityCountry: Yup.object(),
    Country: Yup.object(),
    Email: Yup.string().email(i18n.t('fields.errors.validation.emailFormat')),
    Password: Yup.string().matches(
      /(?=.{8,})((?=.*\d)(?=.*[a-z])(?=.*[A-Z])|(?=.*\d)(?=.*[a-zA-Z])(?=.*[\W_])|(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_])).*/,
      i18n.t(`fields.errors.validation.passwordStrength`),
    ),
    SSN: Yup.string().when('SSN', (val) => {
      if (val) {
        return Yup.string()
          .test(i18n.t('fields.errors.validation.ssnDigits'), (value) =>
            /^(?!(000|666|999))\d{3}-?(?!(00|66|99))\d{2}-?(?!(0000|6666|9999))\d{4}$/.test(
              value,
            ),
          )
          .min(11, i18n.t('fields.errors.validation.einValidation'))
          .max(11, i18n.t('fields.errors.validation.einValidation'));
      }
      return Yup.string().nullable(true);
      // checking self-equality works for NaN, transforming it to null
    }),
    NoEin: Yup.bool(),
    Ein: Yup.string().when('NoEin', {
      is: false,
      then: Yup.string().test(
        i18n.t('fields.errors.validation.einDigits'),
        (value) =>
          /^(?!(00|66|99))\d{2}-?(?!(0000000|6666666|9999999))\d{7}$/.test(
            value,
          ),
      ),
      finally: Yup.string().nullable(),
    }),
    BusinessName: Yup.string(),
    TrustName: Yup.string(),
    Amount: noValidation
      ? Yup.number(i18n.t('fields.errors.validation.mustBeNumber')).positive(
          i18n.t('fields.errors.validation.greaterThanZero'),
        )
      : Yup.number(i18n.t('fields.errors.validation.mustBeNumber'))
          .positive(i18n.t('fields.errors.validation.greaterThanZero'))
          .min(
            minAmount,
            i18n.t('fields.errors.validation.minAmount', {
              minAmount: formatMoney(minAmount, 0, 0),
              type,
            }),
          )
          .max(
            maxAmount,
            i18n.t('fields.errors.validation.maxAmount', {
              maxAmount: formatMoney(maxAmount, 0, 0),
              type,
            }),
          ),
    GoldPercentage: Yup.number(i18n.t('fields.errors.validation.mustBeNumber')),
    SilverPercentage: Yup.number(
      i18n.t('fields.errors.validation.mustBeNumber'),
    ),
    percentage: Yup.number(),
    Street: Yup.string().when('Street', (val) => {
      if (val) {
        return Yup.string()
          .typeError(i18n.t('fields.errors.validation.streetAddress'))
          .required();
      }
      return Yup.string().nullable(true);
    }),
    AptOrSuite: Yup.string().nullable(),
    City: Yup.string().when('City', (val) => {
      if (val) {
        return Yup.string()
          .typeError(i18n.t('fields.errors.validation.city'))
          .required();
      }
      return Yup.string().nullable(true);
    }),
    PostalCode: Yup.string().when('PostalCode', (val) => {
      if (val) {
        return Yup.string()
          .typeError(i18n.t('fields.errors.validation.postalCode'))
          .required(i18n.t('fields.errors.validation.postalCode'));
      }
      return Yup.string().nullable(true);
    }),
    // State: Yup.object().when('Country', (val) => {
    //   if (val) {
    //     if (countryCode === 'USA' || countryCode === 'CAN')
    //       Yup.object()
    //         .typeError(i18n.t(`fields.errors.validation.stateRequired`))
    //         .shape({
    //           value: Yup.string()
    //             .typeError(i18n.t(`fields.errors.validation.stateRequired`))
    //             .required(i18n.t('fields.errors.validation.stateRequired')),
    //         });
    //     else {
    //       Yup.string()
    //         .typeError(i18n.t('fields.errors.validation.stateRequired'))
    //         .required(i18n.t('fields.errors.validation.stateRequired'));
    //     }
    //     return Yup.string().nullable(true);
    //   }
    // }),
    AutoDeposit: Yup.boolean().oneOf([true]),
    FundAmount: noValidation
      ? Yup.number(i18n.t('fields.errors.validation.mustBeNumber')).positive(
          i18n.t('fields.errors.validation.greaterThanZero'),
        )
      : Yup.number(i18n.t('fields.errors.validation.mustBeNumber'))
          .min(
            minAmount,
            i18n.t('fields.errors.validation.minAmount', {
              minAmount: formatMoney(minAmount, 0, 0),
              type,
            }),
          )
          .max(
            maxAmount,
            i18n.t('fields.errors.validation.maxAmount', {
              maxAmount: formatMoney(maxAmount, 0, 0),
              type,
            }),
          )
          .positive(i18n.t('fields.errors.validation.greaterThanZero')),
    CustodianName: Yup.string(),
    AccountNumber: Yup.string(),

    CustodianPhone: Yup.string(),
    PersonPhoneNumber: Yup.string(),
    CustodianFax: Yup.string(),
    AccountType: Yup.object().shape({
      value: Yup.string(),
      label: Yup.string(),
    }),
    Custodian: Yup.string(),

    // .shape({
    //   value: Yup.string()
    //     .typeError(i18n.t('fields.errors.validation.custodian'))
    //     .required(i18n.t('fields.errors.validation.custodian')),
    //   label: Yup.string()
    //     .typeError(i18n.t('fields.errors.validation.custodian'))
    //     .required(i18n.t('fields.errors.validation.custodian')),
    // }),
  };

  const validationSchema = fields.reduce(
    (acc, field) => ({
      ...acc,
      [field]: allValidationImpersonateBucket[field],
    }),
    {},
  );

  return validationSchema;
};
